import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
  Input,
  FormFeedback,
} from "reactstrap";
import { validations } from "validations/FormValidations";
import { ScreeningMaster } from "../helper/studyscreening.types";
import { decisionCardBase, decisionCardSelected, decisionIconStyle } from "../helper/studyscreening.constant";
import { formatDate } from "common/service/CommonService";

/* ═══════════════════════════════════════════════════════════
   Props
   ═══════════════════════════════════════════════════════════ */

interface ScreeningDecisionModalProps {
  isOpen: boolean;
  toggle: () => void;
  selectedScreening: ScreeningMaster | null;
  decisionStatus: "passed" | "failed" | null;
  setDecisionStatus: (status: "passed" | "failed") => void;
  reasonforunfit: string;
  setReasonforunfit: (val: string) => void;
  onSave: () => void;
  isPending: boolean;
}

/* ═══════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════ */

const ScreeningDecisionModal: React.FC<ScreeningDecisionModalProps> = ({
  isOpen,
  toggle,
  selectedScreening,
  decisionStatus,
  setDecisionStatus,
  reasonforunfit,
  setReasonforunfit,
  onSave,
  isPending,
}) => {
  const validationError =
    decisionStatus === "failed" && reasonforunfit
      ? validations.studyScreening.reasonforunfit.validate(reasonforunfit)
      : true;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      backdrop="static"
      className="common_modal"
    >
      <ModalHeader toggle={toggle} className="p-3 border-bottom">
        <div className="d-flex align-items-center gap-2">
          <i
            className="ri-scales-3-line text-primary"
            style={{ fontSize: 20 }}
          />
          <span>Screening Decision</span>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        {/* Screening Info */}
        {selectedScreening && (
          <div
            style={{
              background: "#f8f9fa",
              borderRadius: 10,
              padding: "12px 16px",
              marginBottom: 20,
              border: "1px solid #e9ecef",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <small
                  className="text-muted d-block"
                  style={{ fontSize: 11 }}
                >
                  Screening Name
                </small>
                <span className="fw-semibold" style={{ fontSize: 14 }}>
                  {selectedScreening.studyscreeningname}
                </span>
              </div>
              <div className="text-end">
                <small
                  className="text-muted d-block"
                  style={{ fontSize: 11 }}
                >
                  Expiry
                </small>
                <span className="fw-medium" style={{ fontSize: 13 }}>
                  {selectedScreening.expirydate
                    ? formatDate(selectedScreening.expirydate)
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Decision Label */}
        <label
          className="fw-semibold d-block mb-2"
          style={{ fontSize: 13, color: "#495057" }}
        >
          Select Decision <span className="text-danger">*</span>
        </label>

        {/* Decision Cards */}
        <div className="d-flex gap-3 mb-4">
          {/* PASS Card */}
          <div
            style={
              decisionStatus === "passed"
                ? decisionCardSelected("passed")
                : decisionCardBase
            }
            onClick={() => {
              setDecisionStatus("passed");
              setReasonforunfit("");
            }}
            onMouseEnter={(e) => {
              if (decisionStatus !== "passed") {
                e.currentTarget.style.borderColor = "#b7dfbf";
                e.currentTarget.style.background = "#f8fdf9";
              }
            }}
            onMouseLeave={(e) => {
              if (decisionStatus !== "passed") {
                e.currentTarget.style.borderColor = "#e9ecef";
                e.currentTarget.style.background = "#fff";
              }
            }}
          >
            <i
              className="ri-checkbox-circle-fill"
              style={decisionIconStyle("passed", decisionStatus === "passed")}
            />
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: decisionStatus === "passed" ? "#0a7c42" : "#495057",
              }}
            >
              Pass
            </span>
            <small
              className="text-muted text-center"
              style={{ fontSize: 11, lineHeight: 1.3 }}
            >
              Eligible for BA/BE study
            </small>
          </div>

          {/* FAIL Card */}
          <div
            style={
              decisionStatus === "failed"
                ? decisionCardSelected("failed")
                : decisionCardBase
            }
            onClick={() => setDecisionStatus("failed")}
            onMouseEnter={(e) => {
              if (decisionStatus !== "failed") {
                e.currentTarget.style.borderColor = "#f5c6c6";
                e.currentTarget.style.background = "#fffaf9";
              }
            }}
            onMouseLeave={(e) => {
              if (decisionStatus !== "failed") {
                e.currentTarget.style.borderColor = "#e9ecef";
                e.currentTarget.style.background = "#fff";
              }
            }}
          >
            <i
              className="ri-close-circle-fill"
              style={decisionIconStyle("failed", decisionStatus === "failed")}
            />
            <span
              style={{
                fontWeight: 600,
                fontSize: 15,
                color: decisionStatus === "failed" ? "#c9252d" : "#495057",
              }}
            >
              Fail
            </span>
            <small
              className="text-muted text-center"
              style={{ fontSize: 11, lineHeight: 1.3 }}
            >
              Not eligible for BA/BE study
            </small>
          </div>
        </div>

        {/* Reason for unfit (only when Fail is selected) */}
        {decisionStatus === "failed" && (
          <div className="mt-3">
            <label
              className="fw-semibold d-block mb-2"
              style={{ fontSize: 13, color: "#495057" }}
            >
              Reason for Unfit <span className="text-danger">*</span>
            </label>
            <Input
              type="textarea"
              rows={3}
              placeholder="Enter reason for unfit..."
              value={reasonforunfit}
              onChange={(e) => setReasonforunfit(e.target.value)}
              className="form-control"
              invalid={validationError !== true}
            />
            {validationError !== true && (
              <FormFeedback className="d-block">
                {validationError}
              </FormFeedback>
            )}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="bg-white border-top">
        <Button color="dark" onClick={toggle} outline>
          Cancel
        </Button>
        <Button
          color="primary"
          outline
          disabled={
            !decisionStatus ||
            isPending ||
            (decisionStatus === "failed" && (!reasonforunfit.trim() || validationError !== true))
          }
          onClick={onSave}
        >
          {isPending && <Spinner size="sm" className="me-2" />}
          Save Decision
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ScreeningDecisionModal;